import React, { useEffect, useMemo, useState } from "react";

import {db} from '../../firebaseConfig';

import { addDoc, collection, serverTimestamp } from "firebase/firestore";

import { getUserSystemInfo } from "../../common/Authenticator";

/**
 * Pawsyly - Reddit /demo page
 * Goal: Build trust + show workflow (no heavy sales), then offer a soft "try it" CTA.
 *
 * ✅ Embed your video (YouTube unlisted recommended)
 * ✅ Do NOT send users to YouTube directly; embed it here
 * ✅ Keep one primary CTA: "Try it yourself"
 *
 * Replace DEMO_VIDEO with your actual embed URL.
 * Example YouTube embed format:
 * https://www.youtube.com/embed/VIDEO_ID?rel=0&modestbranding=1&playsinline=1
 */

const APP_CONFIG = {
  brand: "Pawsyly®",
  accent: "#a64d79",

  headline: "This is how I actually use Pawsyly for my pet sitting business.",
  subHeadline:
    "No pitch. Just a walkthrough of the setup I use day to day.",

  videoTitle: "Quick walkthrough (about 6 minutes)",
  videoSub:
    "I show how I keep client info, pet details, bookings, and reminders in one place — so I’m not carrying everything in my head.",

  youWillSeeTitle: "In this walkthrough, I show:",
  youWillSeeBullets: [
    "How I keep client info + pet details in one place",
    "How bookings and schedules don’t overlap",
    "How reminders stop me from missing things",
    "How I reduce the constant mental load",
  ],

  notForEveryoneTitle: "This probably isn’t for you if:",
  notForEveryoneBullets: [
    "You only pet sit occasionally",
    "You’re happy juggling notes, texts, and reminders",
    "You don’t mind double-checking everything",
  ],

  ctaTitle: "Want to try it with your own clients?",
  ctaButton: "Try it yourself (free)",
  ctaSub:
    "Takes about 2 minutes to get set up. You can delete it if it’s not useful.",

  footerNote:
    "Thanks for checking it out. I built this for my own workflow, and I’m sharing it in case it helps someone else.",

  // ✅ Replace this with your real video embed URL
  // YouTube unlisted recommended:
  // "https://www.youtube.com/embed/VIDEO_ID?rel=0&modestbranding=1&playsinline=1"
  demoVideoEmbedUrl: "https://www.youtube.com/embed/gcYnE5OXcig?si=6ZHyGsraXN6o6k2D",

  // Where the CTA sends users to actually try it
  signupUrl: "/signup?sourcepage=demo",
};

const DemoPage = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {

    if (window.fbq) {
      
      window.fbq('track', 'ViewContent', {
        app: 'pawsyly'
      });

      window.fbq('trackCustom', 'ViewDemo', {
        app: 'pawsyly'
      });

    }

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const responsiveStyles = useMemo(() => {
    return {
      ...styles,
      grid: {
        ...styles.grid,
        gridTemplateColumns: isMobile ? "1fr" : "1.35fr 0.65fr",
        gap: isMobile ? "28px" : "40px",
      },
      main: {
        ...styles.main,
        padding: isMobile ? "40px 20px" : "60px 40px",
      },
      headline: {
        ...styles.headline,
        fontSize: isMobile ? "32px" : "46px",
      },
    };
  }, [isMobile]);

  const safeEmbedUrl = APP_CONFIG.demoVideoEmbedUrl || "";
  const hasVideo =
    safeEmbedUrl.includes("youtube.com/embed/") ||
    safeEmbedUrl.includes("player.vimeo.com/video/") ||
    safeEmbedUrl.includes("iframe");


    const getSessionId = () => {
    let id = sessionStorage.getItem('ppcSessionId');
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem('ppcSessionId', id);
    }
    return id;
  };

  const handleTryIt = async () => {
    const sessionId = getSessionId();

    const systemInfo = await getUserSystemInfo();

    await addDoc(collection(db, "appFunnelEvents"), {
      event: "go_to_signup",
      app: "pawsyly",
      source: "reddit",
      sessionId,
      page: "/reddit",
      timestamp: serverTimestamp(),
      systemInfo
    });

    window.location.href = APP_CONFIG.signupUrl;
  };


  return (
    <div style={responsiveStyles.container}>
      <main style={responsiveStyles.main}>
        {/* HERO */}
        <section style={responsiveStyles.hero}>
          <div style={responsiveStyles.brandRow}>
            <div style={responsiveStyles.brandDot} />
            <span style={responsiveStyles.brandText}>{APP_CONFIG.brand}</span>
          </div>

          <h1 style={responsiveStyles.headline}>{APP_CONFIG.headline}</h1>
          <p style={responsiveStyles.subHeadline}>{APP_CONFIG.subHeadline}</p>
        </section>

        {/* CONTENT GRID */}
        <section style={responsiveStyles.grid}>
          {/* LEFT: VIDEO + CONTENT */}
          <div style={responsiveStyles.left}>
            {/* VIDEO CARD */}
            <div style={responsiveStyles.videoCard}>
              <div style={responsiveStyles.videoHeader}>
                <h2 style={responsiveStyles.videoTitle}>
                  {APP_CONFIG.videoTitle}
                </h2>
                <p style={responsiveStyles.videoSub}>{APP_CONFIG.videoSub}</p>
              </div>

              <div style={responsiveStyles.videoFrameWrap}>
                {hasVideo ? (
                  <iframe
                    title="Pawsyly walkthrough"
                    src={safeEmbedUrl}
                    style={responsiveStyles.videoFrame}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                ) : (
                  <div style={responsiveStyles.videoPlaceholder}>
                    <p style={responsiveStyles.videoPlaceholderTitle}>
                      Add your video embed URL
                    </p>
                    <p style={responsiveStyles.videoPlaceholderText}>
                      Replace <code>APP_CONFIG.demoVideoEmbedUrl</code> with a YouTube
                      unlisted embed link (recommended) or Vimeo embed link.
                    </p>
                    <div style={responsiveStyles.codeBlock}>
                      https://www.youtube.com/embed/VIDEO_ID?rel=0&amp;modestbranding=1&amp;playsinline=1
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* WHAT YOU’LL SEE */}
            <div style={responsiveStyles.card}>
              <h3 style={responsiveStyles.cardTitle}>
                {APP_CONFIG.youWillSeeTitle}
              </h3>
              <ul style={responsiveStyles.bulletList}>
                {APP_CONFIG.youWillSeeBullets.map((item, idx) => (
                  <li key={idx} style={responsiveStyles.bulletItem}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* NOTE */}
            <div style={responsiveStyles.noteCard}>
              <div style={responsiveStyles.noteBar} />
              <div>
                <p style={responsiveStyles.noteText}>{APP_CONFIG.footerNote}</p>
              </div>
            </div>
          </div>

          {/* RIGHT: CTA + NOT FOR EVERYONE */}
          <div style={responsiveStyles.right}>
            {/* CTA BOX */}
            <div style={responsiveStyles.ctaBox}>
              <h3 style={responsiveStyles.ctaTitle}>{APP_CONFIG.ctaTitle}</h3>

              <button
                style={responsiveStyles.ctaButton}
                onClick={handleTryIt}

                onMouseOver={(e) =>
                  (e.currentTarget.style.backgroundColor = "#c24a22")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.backgroundColor = "#d85a2f")
                }
              >
                {APP_CONFIG.ctaButton}
              </button>

              <p style={responsiveStyles.ctaSub}>{APP_CONFIG.ctaSub}</p>
            </div>

            {/* NOT FOR EVERYONE */}
            <div style={responsiveStyles.card}>
              <h3 style={responsiveStyles.cardTitle}>
                {APP_CONFIG.notForEveryoneTitle}
              </h3>
              <ul style={responsiveStyles.bulletList}>
                {APP_CONFIG.notForEveryoneBullets.map((item, idx) => (
                  <li key={idx} style={responsiveStyles.bulletItemMuted}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* SECONDARY HELP */}
            <div style={responsiveStyles.smallCard}>
              <h4 style={responsiveStyles.smallCardTitle}>Prefer reading?</h4>
              <p style={responsiveStyles.smallCardText}>
                If you’d rather not watch the video, the “Try it yourself” button
                takes you straight into setup. No pressure either way.
              </p>
            </div>
          </div>
        </section>
      </main>
      <div>
                                <center>
                      <table>
                        <tr>
                          <td>
                            <a href="privacy.html" target="_blank">
                                               <p>Privacy Policy</p>
                                            </a>
                          </td>
                          <td>
                            &nbsp;&nbsp;&nbsp;
                          </td>
                          <td>
                                 <a href="terms-of-service.html"  target="_blank">
                                                <p>Terms and Conditions</p>
                                  </a>
                          </td>
                          <td>
                            &nbsp;&nbsp;&nbsp;
                          </td>
                   
                          </tr>
                          </table>
                          <table>
                          <tr colspan="3">
                            <td style={{textAlign:'center'}}>
                              <p>&#169; 2026 Copyright Pawsyly<sup style={{fontSize:'12px'}}>&#174;</sup></p>
                            </td>
                          </tr>
                        
                      </table>
                    </center>   

      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: "#f6f6f6",
    minHeight: "100vh",
    fontFamily:
      '"Source Serif 4", Georgia, "Times New Roman", serif', //-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif
    color: "#1a1a1a",
  },
  main: {
    maxWidth: "1100px",
    margin: "0 auto",
    padding: "60px 40px",
  },
  hero: {
    marginBottom: "32px",
  },
  brandRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "14px",
  },
  brandDot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: "#a64d79",
  },
  brandText: {
    fontSize: "14px",
    fontWeight: "700",
    letterSpacing: "0.02em",
    color: "#333",
  },
  headline: {
    fontSize: "46px",
    fontWeight: "800",
    lineHeight: "1.1",
    margin: "0 0 12px 0",
  },
  subHeadline: {
    fontSize: "18px",
    lineHeight: "1.6",
    margin: "0",
    color: "#333",
    maxWidth: "760px",
  },

  grid: {
    display: "grid",
    alignItems: "start",
  },

  left: {
    display: "flex",
    flexDirection: "column",
    gap: "22px",
  },
  right: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },

  videoCard: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "10px",
    overflow: "hidden",
  },
  videoHeader: {
    padding: "22px 22px 0 22px",
  },
  videoTitle: {
    fontSize: "20px",
    fontWeight: "800",
    margin: "0 0 8px 0",
  },
  videoSub: {
    fontSize: "15px",
    color: "#555",
    lineHeight: "1.6",
    margin: "0 0 16px 0",
  },
  videoFrameWrap: {
    padding: "0 22px 22px 22px",
  },
  videoFrame: {
    width: "100%",
    aspectRatio: "16 / 9",
    border: "0",
    borderRadius: "10px",
    backgroundColor: "#000",
  },
  videoPlaceholder: {
    width: "100%",
    aspectRatio: "16 / 9",
    borderRadius: "10px",
    border: "1px dashed #bbb",
    backgroundColor: "#fafafa",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "18px",
  },
  videoPlaceholderTitle: {
    margin: "0 0 10px 0",
    fontWeight: "800",
  },
  videoPlaceholderText: {
    margin: "0 0 12px 0",
    color: "#555",
    lineHeight: "1.6",
  },
  codeBlock: {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: "12px",
    backgroundColor: "#fff",
    border: "1px solid #eee",
    borderRadius: "8px",
    padding: "10px",
    color: "#333",
    overflowX: "auto",
  },

  card: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "10px",
    padding: "22px",
  },
  cardTitle: {
    fontSize: "18px",
    fontWeight: "800",
    margin: "0 0 12px 0",
  },
  bulletList: {
    margin: "0",
    paddingLeft: "20px",
  },
  bulletItem: {
    fontSize: "15px",
    lineHeight: "1.7",
    color: "#222",
    marginBottom: "8px",
  },
  bulletItemMuted: {
    fontSize: "15px",
    lineHeight: "1.7",
    color: "#444",
    marginBottom: "8px",
  },

  ctaBox: {
    backgroundColor: "#e8ddd3",
    borderRadius: "10px",
    padding: "22px",
  },
  ctaTitle: {
    fontSize: "18px",
    fontWeight: "900",
    margin: "0 0 14px 0",
  },
  ctaButton: {
    width: "100%",
    padding: "16px 18px",
    fontSize: "16px",
    fontWeight: "700",
    borderRadius: "6px",
    border: "none",
    backgroundColor: "#d85a2f",
    color: "#fff",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  ctaSub: {
    margin: "12px 0 0 0",
    fontSize: "13px",
    lineHeight: "1.55",
    color: "#555",
  },

  noteCard: {
    display: "flex",
    gap: "14px",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "10px",
    padding: "18px",
  },
  noteBar: {
    width: "6px",
    borderRadius: "999px",
    backgroundColor: "#a64d79",
  },
  noteText: {
    margin: "0",
    fontSize: "14px",
    lineHeight: "1.65",
    color: "#444",
  },

  smallCard: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: "10px",
    padding: "18px",
  },
  smallCardTitle: {
    margin: "0 0 8px 0",
    fontSize: "14px",
    fontWeight: "900",
  },
  smallCardText: {
    margin: "0",
    fontSize: "14px",
    lineHeight: "1.6",
    color: "#555",
  },
};

export default DemoPage;
